/**
 * DebugContext
 *
 * Holds debugging options (e.g. enable toasts, enable logging, log hub handlers)
 * as state so they can be toggled at runtime. Only users whose IDs are in
 * DEBUG_ALLOWED_USER_IDS can see and change these options.
 */

// ============= Imports =============

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';

// ============= Constants =============

/**
 * User IDs that are allowed to see and change debug options.
 * Add more IDs to this array as needed.
 */
export const DEBUG_ALLOWED_USER_IDS: string[] = [
  'ffa54f2a-ec40-43ca-b9c4-0e6d0876a6ff',
];

// ============= Types =============

export interface DebugState {
  /** Show toasts for SignalR relay hub events (e.g. USER_ONLINE). */
  enableToasts: boolean;
  /** Log SignalR relay hub events to console. */
  enableLogging: boolean;
  /** Log registered hub handlers summary (e.g. in App when UserRelayHub connects). */
  logHubHandlers: boolean;
}

export interface DebugContextProps {
  /** Whether the current user is allowed to see and change debug options. */
  canShowDebug: boolean;
  /** Current debug state. */
  debug: DebugState;
  /** Update a single debug flag. */
  setDebugFlag: <K extends keyof DebugState>(key: K, value: DebugState[K]) => void;
  /** Allowed user IDs (for reference). */
  allowedUserIds: string[];
}

// ============= Context =============

const DebugContext = createContext<DebugContextProps | undefined>(undefined);

// ============= Hook =============

export const useDebug = (): DebugContextProps => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};

/** Optional hook: returns null if outside provider (e.g. tests). */
export const useDebugOptional = (): DebugContextProps | null => {
  return useContext(DebugContext) ?? null;
};

// ============= Default state & storage =============

const defaultDebugState: DebugState = {
  enableToasts: true,
  enableLogging: false,
  logHubHandlers: false,
};

const DEBUG_STORAGE_KEY = 'litium-debug-state';

function readDebugStateFromStorage(): DebugState {
  if (typeof sessionStorage === 'undefined') return defaultDebugState;
  try {
    const raw = sessionStorage.getItem(DEBUG_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DebugState>;
      return { ...defaultDebugState, ...parsed };
    }
  } catch {
    // ignore invalid or missing data
  }
  return defaultDebugState;
}

function writeDebugStateToStorage(state: DebugState): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(DEBUG_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota or other errors
  }
}

// ============= Provider =============

export interface DebugProviderProps {
  children: React.ReactNode;
  /** Override allowed user IDs (e.g. from config). If not set, uses DEBUG_ALLOWED_USER_IDS. */
  allowedUserIds?: string[];
  /** Current user ID to determine canShowDebug. */
  currentUserId: string | null;
}

export const DebugProvider: React.FC<DebugProviderProps> = ({
  children,
  allowedUserIds = DEBUG_ALLOWED_USER_IDS,
  currentUserId,
}) => {
  const [debug, setDebugState] = useState<DebugState>(readDebugStateFromStorage);

  const setDebugFlag = useCallback(<K extends keyof DebugState>(
    key: K,
    value: DebugState[K]
  ) => {
    setDebugState((prev) => {
      const next = { ...prev, [key]: value };
      writeDebugStateToStorage(next);
      return next;
    });
  }, []);

  const canShowDebug =
    currentUserId != null &&
    currentUserId !== '' &&
    allowedUserIds.includes(currentUserId);

  const value = useMemo<DebugContextProps>(
    () => ({
      canShowDebug,
      debug,
      setDebugFlag,
      allowedUserIds,
    }),
    [canShowDebug, debug, setDebugFlag, allowedUserIds]
  );

  return (
    <DebugContext.Provider value={value}>
      {children}
    </DebugContext.Provider>
  );
};
